/**
 * /reset-password?token=... — consume a reset token and set a new password.
 *
 * The raw token from the email link is sent to the backend, which compares
 * its SHA-256 hash against the stored hash. On success the backend bumps the
 * user's token_version (force-logout of all sessions), so we also clear any
 * local token and send the user to /login to sign in fresh.
 *
 * NOT wrapped in PublicRoute (like /verify-email) so a user who clicks the
 * link while still signed in on another tab isn't bounced to their dashboard.
 *
 * Zero dependencies on dispatch / state machine / janitor / offer routing.
 */
import React, { useState } from "react";
import { useSearchParams, useNavigate, Link } from "react-router-dom";
import { api, tokenStore, formatApiError } from "../lib/api";
import { Button } from "../components/ui/button";
import { PasswordInput } from "../components/ui/password-input";
import { Label } from "../components/ui/label";
import { toast } from "sonner";
import { KeyRound, AlertTriangle, CheckCircle2 } from "lucide-react";

export default function ResetPassword() {
  const [search] = useSearchParams();
  const token = search.get("token") || "";
  const navigate = useNavigate();

  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [loading, setLoading] = useState(false);
  // phase: form | done | invalid | expired
  const [phase, setPhase] = useState(token ? "form" : "invalid");

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (password.length < 8) {
      toast.error("Password must be at least 8 characters.");
      return;
    }
    if (password !== confirm) {
      toast.error("Passwords don't match.");
      return;
    }
    setLoading(true);
    try {
      await api.post("/auth/reset-password", { token, new_password: password });
      // Password changed → existing sessions are revoked server-side. Clear
      // any local token so this device starts from a clean signed-out state.
      tokenStore.clear();
      setPhase("done");
      toast.success("Password reset — please sign in with your new password.");
      setTimeout(() => navigate("/login", { replace: true }), 1600);
    } catch (err) {
      const reason = err?.response?.data?.detail?.reason;
      if (reason === "expired_token") {
        setPhase("expired");
      } else if (reason === "invalid_token") {
        setPhase("invalid");
      } else if (reason === "same_password") {
        toast.error("Your new password must be different from your current one.");
      } else {
        toast.error(formatApiError(err) || "Could not reset your password. Please try again.");
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-background flex items-center justify-center px-4">
      <div className="max-w-md w-full">
        <div className="border border-border bg-card p-8" data-testid="reset-password-card">
          {phase === "form" && (
            <form onSubmit={handleSubmit} className="space-y-6" data-testid="reset-password-form">
              <div>
                <div className="w-10 h-10 rounded-full bg-[#FFC107] flex items-center justify-center mb-3">
                  <KeyRound className="w-5 h-5 text-[#0A0A0A]" />
                </div>
                <h1 className="font-heading text-3xl font-black tracking-tight">Set a new password</h1>
                <p className="text-sm text-muted-foreground mt-2">
                  Choose a strong password you don&apos;t use anywhere else. You&apos;ll be signed
                  out of all devices once it&apos;s changed.
                </p>
              </div>
              <div className="space-y-4">
                <div>
                  <Label htmlFor="new-password" className="label-mini">NEW PASSWORD</Label>
                  <PasswordInput
                    id="new-password"
                    name="new-password"
                    autoComplete="new-password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    placeholder="At least 8 characters"
                    data-testid="reset-password-input"
                    className="mt-1.5"
                  />
                </div>
                <div>
                  <Label htmlFor="confirm-password" className="label-mini">CONFIRM PASSWORD</Label>
                  <PasswordInput
                    id="confirm-password"
                    name="confirm-password"
                    autoComplete="new-password"
                    value={confirm}
                    onChange={(e) => setConfirm(e.target.value)}
                    required
                    placeholder="Re-enter your new password"
                    data-testid="reset-password-confirm-input"
                    className="mt-1.5"
                  />
                </div>
              </div>
              <Button type="submit" disabled={loading} className="w-full" size="lg" data-testid="reset-password-submit-btn">
                {loading ? "Resetting…" : "Reset password"}
              </Button>
            </form>
          )}

          {phase === "done" && (
            <div className="text-center" data-testid="reset-password-done">
              <div className="w-14 h-14 rounded-full bg-[#10B981] mx-auto flex items-center justify-center">
                <CheckCircle2 className="w-8 h-8 text-white" />
              </div>
              <h1 className="font-heading text-2xl font-black mt-4">Password updated</h1>
              <p className="text-sm text-muted-foreground mt-2">Redirecting you to sign in…</p>
            </div>
          )}

          {(phase === "invalid" || phase === "expired") && (
            <div className="text-center" data-testid={`reset-password-${phase}`}>
              <div className={`w-14 h-14 rounded-full mx-auto flex items-center justify-center ${phase === "expired" ? "bg-[#F59E0B]" : "bg-[#EF4444]"}`}>
                <AlertTriangle className="w-8 h-8 text-white" />
              </div>
              <h1 className="font-heading text-2xl font-black mt-4">
                {phase === "expired" ? "Link expired" : "Link no longer valid"}
              </h1>
              <p className="text-sm text-muted-foreground mt-2">
                {phase === "expired"
                  ? "Password reset links expire after 60 minutes."
                  : "This password reset link is invalid or has already been used."}{" "}
                Request a fresh one below.
              </p>
              <Link to="/forgot-password" data-testid="reset-password-request-new">
                <Button className="w-full mt-6" size="lg">Request a new link</Button>
              </Link>
            </div>
          )}

          <div className="mt-6 pt-6 border-t border-border text-center">
            <Link to="/login" className="text-xs text-muted-foreground hover:text-foreground" data-testid="reset-password-back-to-login">
              ← Back to sign in
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}
